import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { getCookie, setCookie } from '@picsart/rc/cookie';

import { ILanguagesCodes } from '@picsart/rc/types/data/languages';
import {
  defaultLanguage as enLanguage,
  languageCookieKey,
  languagesCodesListFull,
} from 'constants/languages';
import specialLandings from 'constants/specialLandings';
import trim from 'utils/trim';
import usePageLanguage from './usePageLanguage';
import usePageSlug from './usePageSlug';

const useLocalization = (
  languagesList = languagesCodesListFull,
  customDefaultLanguagePerPages = specialLandings,
) => {
  const { language: pageLanguage } = usePageLanguage();
  const { slug, languageParam, slugWithQueryParams } = usePageSlug();
  const navigate = useNavigate();

  const specialLanguage = customDefaultLanguagePerPages[`/${slug}`];
  const defaultLanguage = specialLanguage || enLanguage;
  const locale =
    specialLanguage || (languagesList.includes(pageLanguage) && pageLanguage) || defaultLanguage;

  // Get right url for redirection
  const getRedirectUrl = useCallback(
    (lang: string) => {
      const languagePath = lang === enLanguage ? '' : `${lang}/`;
      const redirectionUrl = `${languagePath}${slugWithQueryParams}`;
      return trim(redirectionUrl, '/');
    },
    [slugWithQueryParams],
  );

  // Navigate and store language change
  const setLanguage = useCallback(
    (lang: string, needToSetCookie = true) => {
      if (languagesList.includes(lang as ILanguagesCodes)) {
        if (pageLanguage !== lang) {
          navigate(getRedirectUrl(lang), { replace: true });
        }
        if (needToSetCookie) {
          setCookie(languageCookieKey, lang, '/');
        }
      }
    },
    [getRedirectUrl, languagesList, navigate, pageLanguage],
  );

  // Header navigation language handler. Links are using direct url without language code, redirecting to right localized version.
  const languageGuard = useCallback(
    (lang: string) => {
      if (
        !languageParam &&
        pageLanguage !== enLanguage &&
        languagesList.includes(pageLanguage as ILanguagesCodes)
      ) {
        const url = getRedirectUrl(lang);
        navigate(`/${url}`, { replace: true });
      }
    },
    [getRedirectUrl, languageParam, languagesList, navigate, pageLanguage],
  );

  const handleLanguageChange = useCallback(
    (lang?: string) => {
      // Check if english and absent lang param
      const routeLanguage = languageParam || enLanguage;
      const cookieLanguage = getCookie(languageCookieKey);

      // Check page have translation for mentioned language
      const availableLanguage = languagesList.includes(pageLanguage)
        ? pageLanguage
        : defaultLanguage;

      // Language chooser case
      if (lang && languagesList.includes(lang as ILanguagesCodes) && lang !== pageLanguage) {
        setLanguage(lang, true);
        return;
      }

      // Directly inputed /en/page should redirected to /page
      if (languageParam === enLanguage) {
        const url = getRedirectUrl(languageParam);
        window.history.replaceState(null, null, `/${url}`);
      }

      // Prevent loop for the same language
      if (routeLanguage !== availableLanguage || cookieLanguage !== routeLanguage) {
        // if page doesn't have a localized version we should use the default language
        if (!languagesList.includes(pageLanguage) && routeLanguage !== availableLanguage) {
          setLanguage(defaultLanguage, false);
        } else if (!document.referrer) {
          // Direct URL entry: If a user manually types the URL in the browser's address bar and hits enter to access a web page

          const referrerAvailableLanguage = languagesList.includes(routeLanguage as ILanguagesCodes)
            ? routeLanguage
            : defaultLanguage;
          setLanguage(referrerAvailableLanguage, referrerAvailableLanguage !== cookieLanguage);
        } else {
          languageGuard(pageLanguage);
        }
      }
    },
    [
      defaultLanguage,
      getRedirectUrl,
      languageGuard,
      languageParam,
      languagesList,
      pageLanguage,
      setLanguage,
    ],
  );

  useEffect(() => {
    handleLanguageChange();
  }, [handleLanguageChange]);

  return {
    language: locale,
    chooserData: {
      languagesList,
      currentLanguage: locale,
    },
    setLanguage: handleLanguageChange,
  };
};

export default useLocalization;
